import { ApiError } from '@api/api-client';
import { createSite, deleteSite, listSites, type SiteResponse, updateSite } from '@api/sites-api';
import Breadcrumbs from '@components/breadcrumbs';
import CountryPicker from '@components/country-picker';
import FieldError from '@components/field-error';
import Modal from '@components/modal';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCountries } from '@hooks/use-countries';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type SiteFormData, siteSchema } from '@validation/schemas';
import { Globe, KeyRound, Pencil, Plus, RefreshCw, ShieldCheck, Trash2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { sileo } from 'sileo';

/**
 * Sites are the tenants that own editions, participants and voters. Several may
 * share a country, which is why a country alone no longer identifies an edition.
 */
export default function ManageSites(): React.JSX.Element {
  const queryClient = useQueryClient();
  const { countries } = useCountries();
  const [showAdd, setShowAdd] = useState(false);
  const [editingSite, setEditingSite] = useState<SiteResponse | null>(null);
  const [deletingSite, setDeletingSite] = useState<SiteResponse | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['sites'],
    queryFn: () => listSites(),
    staleTime: 30_000,
  });

  const sites = data ?? [];

  const refetch = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['sites'] });
  }, [queryClient]);

  const getCountry = useCallback(
    (countryId: string) => countries.find((c) => c.id === countryId),
    [countries],
  );

  return (
    <>
      <Breadcrumbs items={[{ label: 'Editions', to: '/editions' }, { label: 'Sites' }]} />

      <div className="page-header">
        <div className="page-header__left">
          <h1 className="page-header__title">Sites</h1>
          <p className="page-header__subtitle">
            {sites.length} site{sites.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="page-header__actions">
          <button type="button" className="btn btn-secondary" onClick={refetch}>
            <RefreshCw size={16} />
            Refresh
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setShowAdd(true)}>
            <Plus size={16} />
            Add Site
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="empty-state">
          <p>Loading sites...</p>
        </div>
      )}

      {error && (
        <div className="page-error" role="alert">
          <span>{error instanceof Error ? error.message : 'Failed to load sites'}</span>
        </div>
      )}

      {!isLoading && !error && sites.length === 0 && (
        <div className="empty-state">
          <Globe size={40} strokeWidth={1} />
          <p>No sites found.</p>
        </div>
      )}

      {!isLoading && !error && sites.length > 0 && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Country</th>
                <th>Sign-in</th>
                <th>PMS</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((site) => {
                const country = getCountry(site.countryId);

                return (
                  <tr key={site.id}>
                    <td>
                      <code className="text-sm">{site.code}</code>
                    </td>
                    <td>{site.name}</td>
                    <td>
                      {country ? (
                        <span className="country-cell">
                          <img
                            src={`/flags/${country.locale}.svg`}
                            alt=""
                            className="country-cell__flag"
                          />
                          {country.name}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {site.authMode === 'sso' ? (
                        <span className="badge badge-active">
                          <ShieldCheck size={12} />
                          SSO
                        </span>
                      ) : (
                        <span className="badge badge-warning">
                          <KeyRound size={12} />
                          Password
                        </span>
                      )}
                    </td>
                    <td>
                      {site.pmsType ? (
                        <span className="text-sm">{site.pmsType}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon btn-sm"
                          data-tooltip="Edit site"
                          aria-label={`Edit site ${site.code}`}
                          onClick={() => setEditingSite(site)}
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon btn-sm"
                          data-tooltip="Delete site"
                          aria-label={`Delete site ${site.code}`}
                          onClick={() => setDeletingSite(site)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <SiteFormModal
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            refetch();
          }}
        />
      )}

      {editingSite && (
        <SiteFormModal
          site={editingSite}
          onClose={() => setEditingSite(null)}
          onSaved={() => {
            setEditingSite(null);
            refetch();
          }}
        />
      )}

      {deletingSite && (
        <DeleteSiteModal
          site={deletingSite}
          onClose={() => setDeletingSite(null)}
          onDeleted={() => {
            setDeletingSite(null);
            refetch();
          }}
        />
      )}
    </>
  );
}

interface SiteFormModalProps {
  site?: SiteResponse;
  onClose: () => void;
  onSaved: () => void;
}

function SiteFormModal({ site, onClose, onSaved }: SiteFormModalProps): React.JSX.Element {
  const isEditing = site !== undefined;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<SiteFormData>({
    resolver: zodResolver(siteSchema),
    defaultValues: {
      code: site?.code ?? '',
      name: site?.name ?? '',
      countryId: site?.countryId ?? '',
      authMode: site?.authMode ?? 'password',
      pmsType: site?.pmsType ?? '',
      votingBaseUrl: site?.votingBaseUrl ?? '',
    },
    mode: 'onBlur',
  });

  const authMode = watch('authMode');

  const onSubmit = async (data: SiteFormData): Promise<void> => {
    setIsSubmitting(true);
    const pmsType = data.pmsType?.trim() ? data.pmsType.trim() : null;
    const votingBaseUrl = data.votingBaseUrl?.trim() ? data.votingBaseUrl.trim() : null;

    try {
      if (isEditing) {
        await updateSite(site.id, { code: data.code, name: data.name, pmsType, votingBaseUrl });
        sileo.success({ title: 'Site updated successfully' });
      } else {
        await createSite({
          code: data.code,
          name: data.name,
          countryId: data.countryId,
          authMode: data.authMode,
          pmsType,
          votingBaseUrl,
        });
        sileo.success({ title: 'Site created successfully' });
      }
      onSaved();
    } catch (error) {
      if (error instanceof ApiError) {
        sileo.error({ title: error.message });
      } else {
        sileo.error({ title: isEditing ? 'Failed to update site' : 'Failed to create site' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEditing ? `Edit Site — ${site.code}` : 'Add Site'}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="site-form"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Site'}
          </button>
        </>
      }
    >
      <form id="site-form" onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
        <div className="modal-form">
          <div className="modal-form__row">
            <div className="form-group">
              <label htmlFor="site-code" className="form-label">
                Code *
              </label>
              <input
                id="site-code"
                type="text"
                placeholder="tuotempo-es"
                className={`form-input${errors.code ? ' form-input--error' : ''}`}
                {...register('code')}
              />
              <FieldError error={errors.code} />
              {!errors.code && (
                <span className="form-hint">
                  The voting site resolves a hostname to this code. Renaming it breaks that site
                  until the voting app is updated too.
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="site-name" className="form-label">
                Name *
              </label>
              <input
                id="site-name"
                type="text"
                placeholder="Tuotempo Spain"
                className={`form-input${errors.name ? ' form-input--error' : ''}`}
                {...register('name')}
              />
              <FieldError error={errors.name} />
            </div>
          </div>

          <div className="form-group">
            <span className="form-label">Country {isEditing ? '' : '*'}</span>
            {isEditing ? (
              <p className="form-hint form-hint--spaced">
                The country cannot be changed. It decides what the site owns, so switching it would
                strand existing editions and voters.
              </p>
            ) : (
              <>
                <Controller
                  name="countryId"
                  control={control}
                  render={({ field }) => (
                    <CountryPicker value={field.value ?? ''} onChange={field.onChange} />
                  )}
                />
                <FieldError error={errors.countryId} />
              </>
            )}
          </div>

          <div className="modal-form__row">
            <div className="form-group">
              <label htmlFor="site-auth-mode" className="form-label">
                Sign-in {isEditing ? '' : '*'}
              </label>
              {isEditing ? (
                <p className="form-hint form-hint--spaced">
                  {site.authMode === 'sso' ? 'SSO' : 'Password'} — cannot be changed after creation.
                </p>
              ) : (
                <>
                  <select
                    id="site-auth-mode"
                    className={`form-select${errors.authMode ? ' form-select--error' : ''}`}
                    {...register('authMode')}
                  >
                    <option value="password">Password (PMS customers)</option>
                    <option value="sso">SSO (marketplace)</option>
                  </select>
                  <FieldError error={errors.authMode} />
                  <span className="form-hint">
                    A country may only have one SSO site, the marketplace brand.
                  </span>
                </>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="site-pms-type" className="form-label">
                PMS type {authMode === 'password' || site?.authMode === 'password' ? '*' : ''}
              </label>
              <input
                id="site-pms-type"
                type="text"
                placeholder="tuotempo"
                disabled={isEditing ? site.authMode === 'sso' : authMode === 'sso'}
                className={`form-input${errors.pmsType ? ' form-input--error' : ''}`}
                {...register('pmsType')}
              />
              <FieldError error={errors.pmsType} />
              {!errors.pmsType && (
                <span className="form-hint">
                  Required for password sites, and must be empty for SSO sites.
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="site-voting-base-url" className="form-label">
                Voting URL
              </label>
              <input
                id="site-voting-base-url"
                type="text"
                placeholder="https://awards.tuotempo.es"
                className={`form-input${errors.votingBaseUrl ? ' form-input--error' : ''}`}
                {...register('votingBaseUrl')}
              />
              <FieldError error={errors.votingBaseUrl} />
              {!errors.votingBaseUrl && (
                <span className="form-hint">
                  Where this site&apos;s voters land. Access links are exported from it, so the
                  export is refused while it is empty.
                </span>
              )}
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
}

interface DeleteSiteModalProps {
  site: SiteResponse;
  onClose: () => void;
  onDeleted: () => void;
}

function DeleteSiteModal({ site, onClose, onDeleted }: DeleteSiteModalProps): React.JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onConfirm = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      await deleteSite(site.id);
      sileo.success({ title: `Site ${site.code} deleted` });
      onDeleted();
    } catch (error) {
      // The API refuses while the site still owns editions, participants or
      // voters, and says how many of each. Surfacing that message verbatim is
      // more useful than a generic failure.
      if (error instanceof ApiError) {
        sileo.error({ title: error.message });
      } else {
        sileo.error({ title: 'Failed to delete site' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Delete Site — ${site.code}`}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={isSubmitting}
            onClick={() => void onConfirm()}
          >
            {isSubmitting ? 'Deleting...' : 'Delete Site'}
          </button>
        </>
      }
    >
      <p>
        Delete <strong>{site.name}</strong>? This is refused while the site still owns any edition,
        participant or voter.
      </p>
    </Modal>
  );
}
